import React from 'react';
import { useHistory } from 'react-router-dom';
import { useNotify, useRefresh, useTranslate } from 'react-admin';
import { Button, FormControlLabel, Switch, Table, TableBody, makeStyles } from "@material-ui/core"
import { NotificationsOffOutlined, Restore } from '@material-ui/icons';
import { AlertTitle } from '@material-ui/lab';
import moment from 'moment-timezone';

import { apiClient, useHttpClient } from '../../../../http';
import { isAuthorized } from '../../../../common/Authorize';
import { SquareAlert as Alert } from '../../../../common/SquareAlert';
import useSyncedPreferences from '../../../../common/useSyncedPreferences';

import { useOfficeWithStore } from '../../../offices/useOffice';
import { CompletedAlert } from '../../../scoresheet/actions/alerts/CompletedAlert';
import { AssignmentsProvider, useAssignmentsContext } from './AssignmentsContext';
import { Assignment } from './Assignment';
import AssignSettings from './AssignSettings';
import GameMessages from './GameMessages';
import GameChips from './GameChips';


const useStyles = makeStyles(theme => ({
  table: {
    tableLayout: 'fixed',
  }
}))

export const EffectiveOfficialsBoolean = (props) => {
  const translate = useTranslate();
  const [effectiveOfficials, setEffectiveOfficials] = useSyncedPreferences('effectiveAssigningOfficials', false);

  return <FormControlLabel
    label={translate('resources.officials.fields.includeSubOffices')}
    control={<Switch checked={effectiveOfficials} onChange={e => setEffectiveOfficials(e.target.checked)} color="primary" />}
    {...props}
  />
}

const ResetAssignerButton = ({ game }) => {
  const refresh = useRefresh();
  const notify = useNotify();
  const translate = useTranslate();

  const canResetAssignRules = isAuthorized(game, 'games', 'resetAssignRules');

  const onClick = async () => {
    await apiClient(`/games/${game.id}/applyAssignRules`, {
      method: 'POST',
      data: {
        resetAssigner: true,
      }
    }).then(() => {
      refresh();
      notify('ra.message.updated');
    }).catch(err => {
      notify('ra.page.error_try_again', 'error')
    })
  }

  return <Button color="primary" size="small" startIcon={<Restore />} onClick={onClick} disabled={!canResetAssignRules}>
    {translate('resources.games.fields.resetAssigner')}
  </Button>
}

const ManageButton = ({ office, focus, scrollTo, ...props }) => {
  const history = useHistory();
  const translate = useTranslate();

  const onClick = () => {
    const getPath = () => {
      if (office?.type === 'League') return `/leagues/${office?.id}/show/settings`
      if (office?.type === 'Tournament') return `/tournaments/${office?.id}/show/settings`
      if (office?.type === 'Cup') return `/cups/${office?.id}/show/settings`
      return `/offices/${office?.id}/show/assigning`
    }

    history.push({
      pathname: getPath(),
      state: { focus, scrollTo }
    });
  }

  return <Button color="primary" size="small" onClick={onClick} {...props}>
    {translate('ra.action.manage')}
  </Button>
}

const NotConfiguredAlert = ({ game }) => {
  const translate = useTranslate();
  const { data: assignSettingReason, loading } = useHttpClient(`/games/${game?.id}/generateAssignSettings?returnReason=true`, null, !!game?.id);
  const { data: office } = useOfficeWithStore(assignSettingReason?.officeId)

  const canViewAssignSystems = isAuthorized(office, 'assignSystems', 'list');
  const canViewAssignRules = isAuthorized(office, 'assignRules', 'list')

  const gameAlertKey = assignSettingReason ? `assigning_reason.${assignSettingReason.code}` : 'status.assigning_not_configured'

  const getAction = () => {
    if (!assignSettingReason) {
      return <ResetAssignerButton game={game} />
    }
    if (assignSettingReason?.code === 'no_system') {
      return <ManageButton office={office} disabled={!canViewAssignSystems || !office} focus="assignSystems" scrollTo="assignSystems" />
    }
    if (assignSettingReason?.code === 'no_office') {
      return <ManageButton office={office} disabled={!canViewAssignRules || !office} focus="assignRules" scrollTo="assignRules" />
    }
    return null;
  }

  if (loading) return null;
  return <Alert action={getAction()} severity="info">{translate(`resources.games.alerts.${gameAlertKey}`, { name: assignSettingReason?.officeName })}</Alert>
}


const GameStatus = () => {
  const { game, settings, assignments } = useAssignmentsContext()
  const translate = useTranslate()

  if (game?.isApproved) return <CompletedAlert square />

  if (!settings) {
    return <NotConfiguredAlert game={game} />
  }

  if (settings.mode === 'Integration') {
    return <Alert severity="info">{translate('resources.games.alerts.status.integration')}</Alert>
  }

  switch (game.status) {
    case 'Conflict':
      return <Alert severity="error">{translate('resources.games.alerts.status.conflict')}</Alert>
    case 'Cancelled':
      return <Alert severity="error">{translate('resources.games.alerts.status.cancelled')}</Alert>
    case 'Postponed':
      return <Alert severity="error">{translate('resources.games.alerts.status.postponed')}</Alert>
    default: break;
  }

  if (moment().isAfter(game.endTime, 'minute')) {
    return <Alert severity="warning">{translate('resources.games.alerts.status.incomplete')}</Alert>
  }

  switch (game.status) {
    case 'Rescheduled':
      return <Alert severity="info">{translate('resources.games.alerts.status.rescheduled')}</Alert>
    case 'Rink Changed':
      return <Alert severity="info">{translate('resources.games.alerts.status.rink_changed')}</Alert>
    default: break;
  }

  if (!assignments?.length) {
    const canManage = isAuthorized(game, 'games', 'assignSettings')
    if (!canManage) return <Alert severity="info">{translate('resources.games.alerts.assignment.no_officials_required.title')}</Alert>
    return <Alert severity="info">
      <AlertTitle>{translate('resources.games.alerts.assignment.no_officials_required.title')}</AlertTitle>
      {translate('resources.games.alerts.assignment.no_officials_required.assign_official')}
    </Alert>
  }

  return null;
}

const AssignmentVisibility = () => {
  const translate = useTranslate();
  const { settings } = useAssignmentsContext();
  if (!settings || settings.status === 'Active') return null;

  return <Alert severity="warning" icon={<NotificationsOffOutlined />}>
    {translate('resources.games.alerts.assignment.status_of_assignments', { status: settings.status })}
  </Alert>
}

const AssignmentsTable = ({ hideMessages = false }) => {
  const { assignments } = useAssignmentsContext()
  const classes = useStyles();

  return <>
    <GameChips />
    <GameStatus />
    <AssignmentVisibility />
    <Table className={classes.table}>
      <TableBody>
        {assignments.map((assignment) => <Assignment key={assignment.id} assignment={assignment} />)}
      </TableBody>
    </Table>
    <Table className={classes.table}>
      <TableBody>
        {!hideMessages && <GameMessages />}
        <AssignSettings />
      </TableBody>
    </Table>
  </>
}

export default ({ gameId, gameOfficeId, hideMessages }) => {
  return <AssignmentsProvider gameId={gameId} gameOfficeId={gameOfficeId}>
    <AssignmentsTable hideMessages={hideMessages} />
  </AssignmentsProvider>
}
